import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { createBrowserRouter, RouterProvider, useRouteError } from "react-router-dom";
import { ErrorScreen } from "../components/shared/ErrorBoundary";
import { PwaUpdateNotice } from "../components/shared/PwaUpdateNotice";
import { LoadingScreen } from "../components/ui/LoadingScreen";
import { Onboarding } from "../features/onboarding/Onboarding";
import { readOnboardingState, retainOnboardingCompletion, type OnboardingState } from "../features/onboarding/onboarding-storage";
import { bootstrapWorkspace, db, DEFAULT_COLLECTIONS, initializeDatabase, metadataRepository, settingsRepository } from "../db";
import { ThemeProvider } from "./providers/ThemeProvider";

const WorkspaceApp = lazy(() => import("./WorkspaceApp"));

function RouteErrorScreen() {
  const cause = useRouteError();
  const error = cause instanceof Error ? cause : new Error("Unexpected route error");
  return <ErrorScreen error={error} />;
}

const router = createBrowserRouter([
  { path: "*", element: <WorkspaceApp />, errorElement: <RouteErrorScreen /> }
]);

function AppContent() {
  const [onboarded, setOnboarded] = useState(() => readOnboardingState().completed);

  if (!onboarded) return <Onboarding onComplete={async (state) => { await migrateOnboardingToDatabase(state); setOnboarded(true); }} />;

  return (
    <Suspense fallback={<LoadingScreen />}>
      <RouterProvider router={router} />
      <PwaUpdateNotice />
    </Suspense>
  );
}

function selectedCollectionNames(state: OnboardingState) {
  const validNames = new Set(DEFAULT_COLLECTIONS.map((collection) => collection.name));
  return state.starterCollections.filter((name): name is (typeof DEFAULT_COLLECTIONS)[number]["name"] => validNames.has(name as (typeof DEFAULT_COLLECTIONS)[number]["name"]));
}

async function migrateOnboardingToDatabase(onboarding: OnboardingState): Promise<void> {
  if (!onboarding.completed || !onboarding.completedAt) return;
  await bootstrapWorkspace(db, { collectionNames: selectedCollectionNames(onboarding) });
  const update: Parameters<typeof settingsRepository.update>[0] = {
    reading: {
      showArabic: onboarding.showArabic,
      showTransliteration: onboarding.showTransliteration
    },
    appearance: { theme: readStoredOnboardingTheme() }
  };
  if (onboarding.location) {
    update.location = {
      source: onboarding.location.source,
      latitude: onboarding.location.latitude,
      longitude: onboarding.location.longitude,
      ...(onboarding.location.accuracy !== undefined ? { accuracyMeters: onboarding.location.accuracy } : {}),
      capturedAt: onboarding.location.updatedAt
    };
  }
  await settingsRepository.update(update);
  await metadataRepository.updateAppMetadata({ onboardingCompletedAt: onboarding.completedAt });
  retainOnboardingCompletion(onboarding.completedAt);
}

function readStoredOnboardingTheme(): "light" | "dark" | "system" {
  const value = localStorage.getItem("dua-workspace:theme");
  return value === "light" || value === "dark" || value === "system" ? value : "system";
}

async function initializeWorkspaceFromOnboarding(): Promise<void> {
  const onboarding = readOnboardingState();
  await initializeDatabase(db, { collectionNames: [] });
  const metadata = await metadataRepository.getAppMetadata();
  if (onboarding.completed && !metadata.onboardingCompletedAt) {
    await migrateOnboardingToDatabase(onboarding);
  }
  if (!onboarding.completed && metadata.onboardingCompletedAt) {
    retainOnboardingCompletion(metadata.onboardingCompletedAt);
  }
}

function DatabaseGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<string>();

  useEffect(() => {
    void initializeWorkspaceFromOnboarding()
      .then(() => setState("ready"))
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Local storage could not be opened.");
        setState("error");
      });
  }, []);

  if (state === "loading") return <LoadingScreen />;
  if (state === "error") {
    return (
      <main className="fatal-error">
        <p className="eyebrow">Local storage unavailable</p>
        <h1>Your workspace could not be opened.</h1>
        <p>{error} Check that private browsing restrictions are not blocking IndexedDB, then reload.</p>
        <button className="button button--primary" onClick={() => window.location.reload()}>Try again</button>
      </main>
    );
  }
  return children;
}

export function App() {
  return <ThemeProvider><DatabaseGate><AppContent /></DatabaseGate></ThemeProvider>;
}
